import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Textarea } from '../components/ui/textarea';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../components/ui/alert-dialog';
import { Badge } from '../components/ui/badge';
import api from '../api/axios';
import { toast } from 'sonner';
import { ThumbsUp, Eye, Trash2, MessageSquare, Send, Reply, Lightbulb, Bug, Zap, MessageCircle } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';

export const Feedback = () => {
  const { activeProject } = useAuth();
  const { t } = useLanguage();
  
  // Status labels with translations
  const STATUS_LABELS = {
    backlog: t.backlog,
    planned: t.planned,
    in_development: t.inDevelopment,
    testing: t.testing,
    completed: t.completed
  };

  // Priority labels with translations
  const PRIORITY_LABELS = {
    low: t.low,
    medium: t.medium,
    high: t.high,
    urgent: t.urgent
  };
  
  // Feedback type labels with translations
  const FEEDBACK_TYPE_LABELS = {
    feature: t.feature,
    bug: t.bug,
    improvement: t.improvement,
    other: t.other
  };
  const [feedbacks, setFeedbacks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [feedbackToDelete, setFeedbackToDelete] = useState(null);
  const [selectedFeedback, setSelectedFeedback] = useState(null);
  const [loadingFeedback, setLoadingFeedback] = useState(false);
  const [comments, setComments] = useState({}); // feedback_id -> comments array
  const [loadingComments, setLoadingComments] = useState({}); // feedback_id -> loading state
  const [newComment, setNewComment] = useState({}); // feedback_id -> comment content
  const [replyingTo, setReplyingTo] = useState({}); // feedback_id -> parent_comment_id
  const [replyContent, setReplyContent] = useState({}); // feedback_id -> { parent_comment_id: content }

  useEffect(() => {
    if (activeProject) {
      fetchFeedbacks();
    }
  }, [activeProject]);

  useEffect(() => {
    // Load comments when feedback detail dialog opens
    if (selectedFeedback) {
      fetchComments(selectedFeedback.id);
    }
  }, [selectedFeedback]);

  const fetchFeedbacks = async () => {
    if (!activeProject) return;
    
    setLoading(true);
    try {
      const response = await api.get(`/feedbacks?project_id=${activeProject.id}`);
      setFeedbacks(response.data);
    } catch (error) {
      console.error('Failed to fetch feedbacks:', error);
      toast.error(t.feedbackLoadFailed);
    } finally {
      setLoading(false);
    }
  };

  const handleVote = async (feedbackId) => {
    try {
      await api.post(`/feedbacks/${feedbackId}/vote`, { vote_type: 'upvote' });
      fetchFeedbacks();
    } catch (error) {
      toast.error(t.voteFailed);
    }
  };

  const handleUpdateStatus = async (feedbackId, status) => {
    try {
      await api.put(`/feedbacks/${feedbackId}`, { status });
      toast.success(t.statusUpdated);
      fetchFeedbacks();
      setSelectedFeedback(null);
    } catch (error) {
      toast.error(t.statusUpdateFailed);
    }
  };

  const handleDeleteClick = (feedback) => {
    setFeedbackToDelete(feedback);
    setShowDeleteDialog(true);
  };

  const handleDeleteConfirm = async () => {
    if (!feedbackToDelete) return;
    
    try {
      await api.delete(`/feedbacks/${feedbackToDelete.id}`);
      toast.success(t.feedbackDeleted);
      setShowDeleteDialog(false);
      setFeedbackToDelete(null);
      fetchFeedbacks();
      setSelectedFeedback(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || t.feedbackDeleteFailed);
      setShowDeleteDialog(false);
      setFeedbackToDelete(null);
    }
  };

  const fetchComments = async (feedbackId) => {
    setLoadingComments(prev => ({ ...prev, [feedbackId]: true }));
    try {
      const url = activeProject
        ? `/feedbacks/${feedbackId}/comments?project_id=${activeProject.id}`
        : `/feedbacks/${feedbackId}/comments`;
      const response = await api.get(url);
      setComments(prev => ({ ...prev, [feedbackId]: response.data }));
    } catch (error) {
      console.error(`Failed to fetch comments for feedback ${feedbackId}:`, error);
      toast.error(t.commentsLoadFailed || 'Yorumlar yüklenemedi');
    } finally {
      setLoadingComments(prev => ({ ...prev, [feedbackId]: false }));
    }
  };

  const handleSubmitComment = async (feedbackId, e) => {
    e.preventDefault();
    const content = newComment[feedbackId]?.trim();
    if (!content) {
      toast.error(t.commentRequired || 'Yorum boş olamaz');
      return;
    }

    try {
      const url = activeProject
        ? `/feedbacks/${feedbackId}/comments?project_id=${activeProject.id}`
        : `/feedbacks/${feedbackId}/comments`;
      const response = await api.post(url, {
        content: content,
        is_internal: false
      });

      // Add new comment to the list
      setComments(prev => ({
        ...prev,
        [feedbackId]: [...(prev[feedbackId] || []), response.data]
      }));

      // Clear comment input
      setNewComment(prev => ({
        ...prev,
        [feedbackId]: ''
      }));

      // Update feedback comment count
      setFeedbacks(prevFeedbacks =>
        prevFeedbacks.map(f =>
          f.id === feedbackId
            ? { ...f, comments_count: (f.comments_count || 0) + 1 }
            : f
        )
      );

      // Update selected feedback if it's the same
      if (selectedFeedback && selectedFeedback.id === feedbackId) {
        setSelectedFeedback(prev => ({
          ...prev,
          comments_count: (prev.comments_count || 0) + 1
        }));
      }

      toast.success(t.commentSubmitted || 'Yorum gönderildi');
    } catch (error) {
      console.error(`Failed to submit comment:`, error);
      toast.error(error.response?.data?.detail || t.commentSubmitFailed || 'Yorum gönderilemedi');
    }
  };

  const handleSubmitReply = async (feedbackId, parentCommentId, e) => {
    e.preventDefault();
    const content = replyContent[feedbackId]?.[parentCommentId]?.trim();
    if (!content) {
      toast.error(t.commentRequired || 'Cevap boş olamaz');
      return;
    }

    try {
      const url = activeProject
        ? `/feedbacks/${feedbackId}/comments?project_id=${activeProject.id}`
        : `/feedbacks/${feedbackId}/comments`;
      const response = await api.post(url, {
        content: content,
        is_internal: false,
        parent_comment_id: parentCommentId
      });

      // Add new reply to the list
      setComments(prev => ({
        ...prev,
        [feedbackId]: [...(prev[feedbackId] || []), response.data]
      }));

      // Clear reply input
      setReplyContent(prev => ({
        ...prev,
        [feedbackId]: {
          ...(prev[feedbackId] || {}),
          [parentCommentId]: ''
        }
      }));

      // Close reply form
      setReplyingTo(prev => ({
        ...prev,
        [feedbackId]: prev[feedbackId] === parentCommentId ? null : prev[feedbackId]
      }));

      // Update feedback comment count
      setFeedbacks(prevFeedbacks =>
        prevFeedbacks.map(f =>
          f.id === feedbackId
            ? { ...f, comments_count: (f.comments_count || 0) + 1 }
            : f
        )
      );

      // Update selected feedback if it's the same
      if (selectedFeedback && selectedFeedback.id === feedbackId) {
        setSelectedFeedback(prev => ({
          ...prev,
          comments_count: (prev.comments_count || 0) + 1
        }));
      }

      toast.success(t.replySubmitted || 'Cevap gönderildi');
    } catch (error) {
      console.error(`Failed to submit reply:`, error);
      toast.error(error.response?.data?.detail || t.replySubmitFailed || 'Cevap gönderilemedi');
    }
  };

  // Organize comments into hierarchical structure
  const organizeComments = (commentsList) => {
    if (!commentsList) return { topLevel: [], replies: {} };
    
    const topLevel = [];
    const replies = {};
    
    commentsList.forEach(comment => {
      if (comment.parent_comment_id) {
        if (!replies[comment.parent_comment_id]) {
          replies[comment.parent_comment_id] = [];
        }
        replies[comment.parent_comment_id].push(comment);
      } else {
        topLevel.push(comment);
      }
    });
    
    // Sort top level and replies by created_at
    topLevel.sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
    Object.keys(replies).forEach(parentId => {
      replies[parentId].sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
    });
    
    return { topLevel, replies };
  };

  return (
    <>
      <Helmet>
        <title>Feedback Board - FeedPanels</title>
        <meta name="description" content="FeedPanels feedback board: view, triage, and respond to feature requests, bug reports, and improvements. Organize by status and priority." />
        <link rel="canonical" href="https://feedpanels.com/dashboard/feedback" />
      </Helmet>
      <>
      <div className="p-4 md:p-6 lg:p-8">
        <div className="mb-4 md:mb-6">
          <h1 className="text-2xl md:text-3xl font-bold text-gray-900 dark:text-white mb-2" data-testid="feedback-page-title">
            {t.feedbackTitle}
          </h1>
          <p className="text-sm md:text-base text-gray-600 dark:text-gray-400">{t.feedbackDescription}</p>
        </div>

        {/* Feedback List */}
        {loading ? (
          <div className="text-center py-12 text-gray-600 dark:text-gray-400">{t.loadingFeedback}</div>
        ) : feedbacks.length === 0 ? (
          <Card className="p-12 text-center">
            <p className="text-gray-500 dark:text-gray-400 mb-4">{t.noFeedbackYet}</p>
            <p className="text-sm text-gray-400 dark:text-gray-500">{t.noFeedbackDescription}</p>
          </Card>
        ) : (
          <div className="grid gap-3 md:gap-4">
            {feedbacks.map((feedback) => (
              <Card
                key={feedback.id}
                className="feedback-card cursor-pointer"
                onClick={() => {
                  // Just open the modal, don't increment views (views are only from public pages)
                  setSelectedFeedback(feedback);
                }}
                data-testid={`feedback-card-${feedback.id}`}
              >
                <CardContent className="p-3 md:p-6">
                  <div className="flex flex-col md:flex-row items-start gap-3 md:gap-4">
                    {/* Vote Button */}
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleVote(feedback.id);
                      }}
                      className="flex flex-col items-center space-y-1 group flex-shrink-0"
                      data-testid={`vote-button-${feedback.id}`}
                    >
                      <div className="w-9 md:w-10 h-9 md:h-10 rounded-lg border-2 border-gray-300 dark:border-gray-600 group-hover:border-cyan-600 group-hover:bg-cyan-50 dark:group-hover:bg-cyan-900 flex items-center justify-center transition-colors">
                        <ThumbsUp className="w-4 md:w-5 h-4 md:h-5 text-gray-600 dark:text-gray-400 group-hover:text-cyan-600 dark:group-hover:text-cyan-400" />
                      </div>
                      <span className="text-xs md:text-sm font-semibold text-gray-700 dark:text-gray-300">{feedback.upvotes}</span>
                    </button>

                    {/* Content */}
                    <div className="flex-1 min-w-0">
                      <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-2 md:gap-0 mb-2">
                        <div className="flex items-center space-x-2 flex-1 min-w-0">
                          {feedback.type === 'bug' && <Bug className="w-4 md:w-5 h-4 md:h-5 text-red-500 flex-shrink-0" />}
                          {feedback.type === 'feature' && <Lightbulb className="w-4 md:w-5 h-4 md:h-5 text-amber-500 flex-shrink-0" />}
                          {feedback.type === 'improvement' && <Zap className="w-4 md:w-5 h-4 md:h-5 text-blue-500 flex-shrink-0" />}
                          {feedback.type === 'other' && <MessageCircle className="w-4 md:w-5 h-4 md:h-5 text-gray-500 flex-shrink-0" />}
                          <h3 className="text-base md:text-lg font-semibold text-gray-900 dark:text-white truncate">{feedback.title}</h3>
                        </div>
                        <div className="flex items-center space-x-1 md:space-x-2 flex-shrink-0">
                          <Badge className={`status-${feedback.status} text-xs md:text-sm`} data-testid={`status-badge-${feedback.id}`}>
                            {STATUS_LABELS[feedback.status]}
                          </Badge>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDeleteClick(feedback);
                            }}
                            className="text-red-600 hover:text-red-700 hover:bg-red-50 h-8 w-8 p-0"
                            data-testid={`delete-feedback-${feedback.id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                      <p className="text-xs md:text-sm text-gray-600 dark:text-gray-400 mb-3 line-clamp-2">{feedback.description}</p>
                      <div className="flex flex-wrap items-center gap-2 md:gap-4 text-xs md:text-sm text-gray-500 dark:text-gray-400">
                        {feedback.type && (
                          <Badge variant="outline" className="text-xs">
                            {feedback.type === 'feature' && (
                              <div className="flex items-center">
                                <Lightbulb className="w-3 h-3 mr-1 text-amber-500" />
                                {FEEDBACK_TYPE_LABELS.feature}
                              </div>
                            )}
                            {feedback.type === 'bug' && (
                              <div className="flex items-center">
                                <Bug className="w-3 h-3 mr-1 text-red-500" />
                                {FEEDBACK_TYPE_LABELS.bug}
                              </div>
                            )}
                            {feedback.type === 'improvement' && (
                              <div className="flex items-center">
                                <Zap className="w-3 h-3 mr-1 text-blue-500" />
                                {FEEDBACK_TYPE_LABELS.improvement}
                              </div>
                            )}
                            {feedback.type === 'other' && (
                              <div className="flex items-center">
                                <MessageCircle className="w-3 h-3 mr-1 text-gray-500" />
                                {FEEDBACK_TYPE_LABELS.other}
                              </div>
                            )}
                          </Badge>
                        )}
                        <div className="flex items-center space-x-1">
                          <Eye className="w-4 h-4" />
                          <span>{feedback.views || 0}</span>
                        </div>
                        {feedback.priority && (
                          <Badge variant="outline" className={`priority-${feedback.priority}`}>
                            {PRIORITY_LABELS[feedback.priority]}
                          </Badge>
                        )}
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Feedback Detail Dialog */}
        <Dialog open={!!selectedFeedback} onOpenChange={() => setSelectedFeedback(null)}>
          <DialogContent className="w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            {loadingFeedback ? (
              <div className="text-center py-8 text-gray-600 dark:text-gray-400">{t.loading}</div>
            ) : selectedFeedback && (
              <>
                <DialogHeader>
                  <DialogTitle className="text-lg md:text-2xl dark:text-white">{selectedFeedback.title}</DialogTitle>
                  <DialogDescription className="text-xs md:text-sm dark:text-gray-400">
                    {t.submittedBy} {selectedFeedback.submitter_email || t.anonymous}
                  </DialogDescription>
                </DialogHeader>
                <div className="space-y-4">
                  <div>
                    <label className="text-xs md:text-sm font-medium text-gray-700 dark:text-gray-300">{t.description}</label>
                    <p className="mt-1 text-xs md:text-sm text-gray-600 dark:text-gray-400">{selectedFeedback.description}</p>
                  </div>
                  <div>
                    <label className="text-xs md:text-sm font-medium text-gray-700 dark:text-gray-300 block mb-2">{t.updateStatus}</label>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                      {Object.entries(STATUS_LABELS).map(([value, label]) => (
                        <Button
                          key={value}
                          variant={selectedFeedback.status === value ? 'default' : 'outline'}
                          size="sm"
                          onClick={() => handleUpdateStatus(selectedFeedback.id, value)}
                          data-testid={`status-button-${value}`}
                          className="text-xs md:text-sm"
                        >
                          {label}
                        </Button>
                      ))}
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-2 md:gap-4 pt-4 border-t dark:border-gray-700">
                    <div>
                      <p className="text-xs md:text-sm text-gray-500 dark:text-gray-400">{t.upvotes}</p>
                      <p className="text-xl md:text-2xl font-bold text-gray-900 dark:text-white">{selectedFeedback.upvotes}</p>
                    </div>
                    <div>
                      <p className="text-xs md:text-sm text-gray-500 dark:text-gray-400">{t.views}</p>
                      <p className="text-xl md:text-2xl font-bold text-gray-900 dark:text-white">{selectedFeedback.views || 0}</p>
                    </div>
                  </div>

                  {/* Comments Section */}
                  <div className="pt-4 md:pt-6 border-t dark:border-gray-700 mt-6">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="text-base md:text-lg font-semibold text-gray-900 dark:text-white flex items-center space-x-2">
                        <MessageSquare className="w-4 md:w-5 h-4 md:h-5 flex-shrink-0" />
                        <span className="text-xs md:text-sm">{t.comments || 'Yorumlar'} ({selectedFeedback.comments_count || 0})</span>
                      </h3>
                    </div>

                    {/* Comments List */}
                    {loadingComments[selectedFeedback.id] ? (
                      <div className="text-sm text-gray-500 dark:text-gray-400 py-4">{t.loading || 'Yükleniyor...'}</div>
                    ) : (
                      <div className="space-y-4 mb-4 max-h-96 overflow-y-auto">
                        {(() => {
                          const { topLevel, replies } = organizeComments(comments[selectedFeedback.id]);
                          if (topLevel.length === 0) {
                            return (
                              <div className="text-sm text-gray-500 dark:text-gray-400 py-4 text-center">
                                {t.noCommentsYet || 'Henüz yorum yok. İlk yorumu siz yapın!'}
                              </div>
                            );
                          }
                          return topLevel.map((comment) => (
                            <div key={comment.id} className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4 shadow-sm hover:shadow-md dark:hover:shadow-lg transition-shadow">
                              <div className="flex items-start justify-between mb-2">
                                <div className="flex items-center space-x-2">
                                  <span className="font-semibold text-gray-900 dark:text-white">
                                    {comment.user_name || comment.commenter_name || t.anonymous}
                                  </span>
                                  {comment.user_role && (
                                    <Badge variant="outline" className="text-xs">
                                      {comment.user_role}
                                    </Badge>
                                  )}
                                  {comment.is_internal && (
                                    <Badge variant="outline" className="text-xs bg-yellow-50">
                                      {t.internal || 'İç'}
                                    </Badge>
                                  )}
                                  {!comment.user_id && (
                                    <Badge variant="outline" className="text-xs bg-blue-50 text-blue-700">
                                      {t.public || 'Public'}
                                    </Badge>
                                  )}
                                </div>
                                <span className="text-xs text-gray-500 dark:text-gray-400">
                                  {new Date(comment.created_at).toLocaleDateString('tr-TR', {
                                    year: 'numeric',
                                    month: 'short',
                                    day: 'numeric',
                                    hour: '2-digit',
                                    minute: '2-digit'
                                  })}
                                </span>
                              </div>
                              <p className="text-gray-700 dark:text-gray-200 text-sm whitespace-pre-wrap mb-3 leading-relaxed">{comment.content}</p>
                              
                              {/* Reply Button */}
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => setReplyingTo(prev => ({
                                  ...prev,
                                  [selectedFeedback.id]: prev[selectedFeedback.id] === comment.id ? null : comment.id
                                }))}
                                className="text-xs"
                              >
                                <Reply className="w-3 h-3 mr-1" />
                                {replyingTo[selectedFeedback.id] === comment.id ? (t.cancelReply || 'İptal') : (t.reply || 'Cevap Ver')}
                              </Button>

                              {/* Reply Form */}
                              {replyingTo[selectedFeedback.id] === comment.id && (
                                <form onSubmit={(e) => handleSubmitReply(selectedFeedback.id, comment.id, e)} className="mt-3 space-y-2 pt-3 border-t dark:border-gray-700">
                                  <Textarea
                                    value={replyContent[selectedFeedback.id]?.[comment.id] || ''}
                                    onChange={(e) => setReplyContent(prev => ({
                                      ...prev,
                                      [selectedFeedback.id]: {
                                        ...(prev[selectedFeedback.id] || {}),
                                        [comment.id]: e.target.value
                                      }
                                    }))}
                                    placeholder={t.writeReply || 'Cevabınızı yazın...'}
                                    rows={2}
                                    className="resize-none text-sm"
                                  />
                                  <div className="flex space-x-2">
                                    <Button type="submit" size="sm" className="text-xs">
                                      <Send className="w-3 h-3 mr-1" />
                                      {t.sendReply || 'Gönder'}
                                    </Button>
                                    <Button
                                      type="button"
                                      variant="ghost"
                                      size="sm"
                                      onClick={() => {
                                        setReplyingTo(prev => ({
                                          ...prev,
                                          [selectedFeedback.id]: null
                                        }));
                                        setReplyContent(prev => ({
                                          ...prev,
                                          [selectedFeedback.id]: {
                                            ...(prev[selectedFeedback.id] || {}),
                                            [comment.id]: ''
                                          }
                                        }));
                                      }}
                                      className="text-xs"
                                    >
                                      {t.cancel || 'İptal'}
                                    </Button>
                                  </div>
                                </form>
                              )}

                              {/* Replies */}
                              {replies[comment.id] && replies[comment.id].length > 0 && (
                                <div className="mt-3 ml-4 pl-4 border-l-2 border-cyan-200 space-y-3">
                                  {replies[comment.id].map((reply) => (
                                    <div key={reply.id} className="bg-white dark:bg-gray-700 rounded-lg p-3 border border-gray-100 dark:border-gray-600">
                                      <div className="flex items-start justify-between mb-2">
                                        <div className="flex items-center space-x-2">
                                          <span className="font-semibold text-gray-900 dark:text-white text-sm">
                                            {reply.user_name || reply.commenter_name || t.anonymous}
                                          </span>
                                          {reply.user_role && (
                                            <Badge variant="outline" className="text-xs">
                                              {reply.user_role}
                                            </Badge>
                                          )}
                                          {reply.is_internal && (
                                            <Badge variant="outline" className="text-xs bg-yellow-50">
                                              {t.internal || 'İç'}
                                            </Badge>
                                          )}
                                          {!reply.user_id && (
                                            <Badge variant="outline" className="text-xs bg-blue-50 text-blue-700">
                                              {t.public || 'Public'}
                                            </Badge>
                                          )}
                                        </div>
                                        <span className="text-xs text-gray-500 dark:text-gray-400">
                                          {new Date(reply.created_at).toLocaleDateString('tr-TR', {
                                            year: 'numeric',
                                            month: 'short',
                                            day: 'numeric',
                                            hour: '2-digit',
                                            minute: '2-digit'
                                          })}
                                        </span>
                                      </div>
                                      <p className="text-gray-700 dark:text-gray-200 text-sm whitespace-pre-wrap mb-2">{reply.content}</p>
                                      
                                      {/* Reply to Reply Button */}
                                      <Button
                                        variant="ghost"
                                        size="sm"
                                        onClick={() => {
                                          setReplyingTo(prev => ({
                                            ...prev,
                                            [selectedFeedback.id]: prev[selectedFeedback.id] === reply.id ? null : reply.id
                                          }));
                                          setReplyContent(prev => ({
                                            ...prev,
                                            [selectedFeedback.id]: {
                                              ...(prev[selectedFeedback.id] || {}),
                                              [reply.id]: prev[selectedFeedback.id]?.[reply.id] || ''
                                            }
                                          }));
                                        }}
                                        className="text-xs"
                                      >
                                        <Reply className="w-3 h-3 mr-1" />
                                        {replyingTo[selectedFeedback.id] === reply.id ? (t.cancelReply || 'İptal') : (t.reply || 'Cevap Ver')}
                                      </Button>

                                      {/* Reply to Reply Form */}
                                      {replyingTo[selectedFeedback.id] === reply.id && (
                                        <form onSubmit={(e) => handleSubmitReply(selectedFeedback.id, reply.id, e)} className="mt-2 pt-2 border-t border-gray-100 dark:border-gray-600 space-y-2">
                                          <Textarea
                                            value={replyContent[selectedFeedback.id]?.[reply.id] || ''}
                                            onChange={(e) => setReplyContent(prev => ({
                                              ...prev,
                                              [selectedFeedback.id]: {
                                                ...(prev[selectedFeedback.id] || {}),
                                                [reply.id]: e.target.value
                                              }
                                            }))}
                                            placeholder={t.writeReply || 'Cevabınızı yazın...'}
                                            rows={2}
                                            className="resize-none text-sm"
                                          />
                                          <div className="flex space-x-2">
                                            <Button type="submit" size="sm" className="text-xs">
                                              <Send className="w-3 h-3 mr-1" />
                                              {t.sendReply || 'Gönder'}
                                            </Button>
                                            <Button
                                              type="button"
                                              variant="ghost"
                                              size="sm"
                                              onClick={() => {
                                                setReplyingTo(prev => ({
                                                  ...prev,
                                                  [selectedFeedback.id]: null
                                                }));
                                                setReplyContent(prev => ({
                                                  ...prev,
                                                  [selectedFeedback.id]: {
                                                    ...(prev[selectedFeedback.id] || {}),
                                                    [reply.id]: ''
                                                  }
                                                }));
                                              }}
                                              className="text-xs"
                                            >
                                              {t.cancel || 'İptal'}
                                            </Button>
                                          </div>
                                        </form>
                                      )}

                                      {/* Nested Replies (replies to replies) */}
                                      {replies[reply.id] && replies[reply.id].length > 0 && (
                                        <div className="mt-2 ml-4 pl-4 border-l-2 border-blue-200 space-y-2">
                                          {replies[reply.id].map((nestedReply) => (
                                            <div key={nestedReply.id} className="bg-gray-50 dark:bg-gray-600 rounded p-2">
                                              <div className="flex items-start justify-between mb-1">
                                                <div className="flex items-center space-x-2">
                                                  <span className="font-semibold text-gray-900 dark:text-white text-xs">
                                                    {nestedReply.user_name || nestedReply.commenter_name || t.anonymous}
                                                  </span>
                                                  {nestedReply.user_role && (
                                                    <Badge variant="outline" className="text-xs">
                                                      {nestedReply.user_role}
                                                    </Badge>
                                                  )}
                                                </div>
                                                <span className="text-xs text-gray-500 dark:text-gray-400">
                                                  {new Date(nestedReply.created_at).toLocaleDateString('tr-TR', {
                                                    year: 'numeric',
                                                    month: 'short',
                                                    day: 'numeric',
                                                    hour: '2-digit',
                                                    minute: '2-digit'
                                                  })}
                                                </span>
                                              </div>
                                              <p className="text-gray-700 dark:text-gray-100 text-xs whitespace-pre-wrap">{nestedReply.content}</p>
                                            </div>
                                          ))}
                                        </div>
                                      )}
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>
                          ));
                        })()}
                      </div>
                    )}

                    {/* Comment Form */}
                    <form onSubmit={(e) => handleSubmitComment(selectedFeedback.id, e)} className="space-y-2 md:space-y-3 pt-4 border-t dark:border-gray-700">
                      <div>
                        <Textarea
                          value={newComment[selectedFeedback.id] || ''}
                          onChange={(e) => setNewComment(prev => ({
                            ...prev,
                            [selectedFeedback.id]: e.target.value
                          }))}
                          placeholder={t.writeComment || 'Yorumunuzu yazın...'}
                          rows={3}
                          className="resize-none text-xs md:text-sm"
                        />
                      </div>
                      <Button type="submit" size="sm" className="w-full md:w-auto text-xs md:text-sm">
                        <Send className="w-3 md:w-4 h-3 md:h-4 mr-2" />
                        {t.submitComment || 'Yorum Gönder'}
                      </Button>
                    </form>
                  </div>
                </div>
              </>
            )}
          </DialogContent>
        </Dialog>

        {/* Delete Confirmation Dialog */}
        <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>{t.deleteFeedback}</AlertDialogTitle>
              <AlertDialogDescription>
                {t.deleteFeedbackConfirm} <strong>"{feedbackToDelete?.title}"</strong>? 
                {t.deleteFeedbackWarning}
                <ul className="list-disc list-inside mt-2 space-y-1">
                  <li>{t.adminPanel}</li>
                  <li>{t.publicPortal}</li>
                  <li>{t.roadmap}</li>
                  <li>{t.allRelatedVotes}</li>
                </ul>
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel onClick={() => setFeedbackToDelete(null)}>{t.cancel}</AlertDialogCancel>
              <AlertDialogAction
                onClick={handleDeleteConfirm}
                className="bg-red-600 hover:bg-red-700 text-white"
              >
                {t.delete}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </>
    </>
  );
};